/**
 * Live Admiralty UK Tidal API state — fetches the station list once for the
 * user's key, finds the nearest station to the boat, and pulls its HW/LW events.
 * In-memory only (not persisted, not committed): we hold the events for display
 * this session and refetch live, respecting the free tier's no-storage terms.
 * The key itself comes from the tide-source store (entered in Settings) and is
 * never written anywhere by us. Absent/invalid key -> idle/error, card falls back.
 */
import { create } from 'zustand';
import {
  fetchAdmiraltyStations,
  fetchAdmiraltyEvents,
  type AdmiraltyEvent,
  type AdmiraltyStation,
} from '@/tides/admiralty';

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface AdmiraltyLiveState {
  stations: AdmiraltyStation[];
  stationId: string | null;
  stationName: string;
  stationCountry: string;
  stationContinuous: boolean | null;
  events: AdmiraltyEvent[];
  status: Status;
  error: string | null;
  /** Last load signature (key tail + station) so we don't refetch the same thing. */
  loadSig: string | null;
  load: (key: string, pos: [number, number]) => Promise<void>;
}

function nearest(stations: AdmiraltyStation[], pos: [number, number]): AdmiraltyStation | null {
  let best: AdmiraltyStation | null = null;
  let bestD = Infinity;
  for (const s of stations) {
    const dx = s.position[0] - pos[0];
    const dy = s.position[1] - pos[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  return best;
}

export const useAdmiraltyStore = create<AdmiraltyLiveState>((set, get) => ({
  stations: [],
  stationId: null,
  stationName: '',
  stationCountry: '',
  stationContinuous: null,
  events: [],
  status: 'idle',
  error: null,
  loadSig: null,

  load: async (key, pos) => {
    const trimmed = key.trim();
    // No Settings key is fine: the dev proxy attaches the env key server-side, so
    // we send no header and let the proxy authenticate. A Settings key overrides.
    const keyTail = trimmed ? trimmed.slice(-6) : 'env';
    const send = trimmed || undefined;
    try {
      // Station list: fetch once, then reuse for nearest-station lookups.
      let stations = get().stations;
      if (stations.length === 0) {
        set({ status: 'loading', error: null });
        stations = await fetchAdmiraltyStations(send);
        set({ stations });
      }
      const st = nearest(stations, pos);
      if (!st) {
        set({ status: 'error', error: 'No Admiralty station found near here.' });
        return;
      }
      const sig = `${keyTail}:${st.id}`;
      if (get().loadSig === sig && get().status === 'ready') return;
      set({ status: 'loading', error: null, stationId: st.id, stationName: st.name, stationCountry: st.country ?? '', stationContinuous: st.continuousHeights ?? null });
      const events = await fetchAdmiraltyEvents(st.id, send);
      set({ events, status: 'ready', error: null, loadSig: sig });
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : 'Admiralty fetch failed' });
    }
  },
}));
